import { MultiViewWidgetContainer, MultiViewWidgetHeader } from './styles';
import { SkeletonLoader } from 'cdk-radial';
import MultiViewWidget from './MultiViewWidget';
import styled from 'styled-components';
import {useCallback} from 'react';
import { useToastStore } from "../components/Toast/useToastStore";
import { useUpdateUserSettings } from './useDashboardQueries';
import useUserSettingStore from './UserSettingStore';
import { useTranslation } from "react-i18next";

const styles = {
  cell: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
};

export const WidgetTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => (theme.darkMode ? 'white' : '#1A1A1A')};
`;

/**
 * Cleans filter by removing empty arrays, null values, and undefined values
 * Returns a clean filter object without empty properties
 */
const cleanFilter = (filter: any): any => {
  if (!filter || typeof filter !== 'object') {
    return null;
  }

  const cleaned: any = {};

  Object.keys(filter).forEach(key => {
    const value = filter[key];

    // Skip undefined and null values
    if (value === undefined || value === null) {
      return;
    }

    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    // Skip empty objects (but keep objects with data)
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
      return;
    }

    // Keep the property
    cleaned[key] = value;
  });

  // Return null if the cleaned object is empty
  return Object.keys(cleaned).length > 0 ? cleaned : null;
};

export const getUpdatedWidgetModel = (dashboardId:string,settings:any,updatedWidget:any,updatedFilter:any) => {
    try {
    const dashboard = settings.dashboard?.find((dash: any) => dash.id === dashboardId);
    const dashboardExists = !!dashboard;
    const widgetId = updatedWidget.id;
    const widgetExists = dashboard?.widgets?.some((widget: any) => widget.id === widgetId);

    // Clean the filter to remove empty arrays and null/undefined values
    const cleanedFilter = cleanFilter(updatedFilter);

    let model = null;

    if (dashboardExists) {
        const updatedDashboard: any = {
            ...dashboard,
            widgets: widgetExists
                // Update existing widget
                ? dashboard.widgets.map((widget: any) =>
                    widget.id === widgetId
                    ? { ...widget, ...updatedWidget }
                    : widget
                )
                // Add new widget
                : [...(dashboard.widgets || []), updatedWidget],
        };

        // Only update filter if we have cleaned data, otherwise preserve existing
        if (cleanedFilter && Object.keys(cleanedFilter).length > 0) {
            updatedDashboard.filter = cleanedFilter;
        } else if (dashboard.filter) {
            // Preserve existing filter if new one is empty
            updatedDashboard.filter = dashboard.filter;
        }

        model = {
            ...settings,
            dashboard: settings.dashboard.map((dash: any) =>
                dash.id === dashboardId ? updatedDashboard : dash
            ),
        };
    } else {
        // Create new dashboard with widget
        const newDashboard: any = {
            id: dashboardId,
            widgets: [updatedWidget],
        };

        // Only include filter if it has valid data after cleaning
        if (cleanedFilter && Object.keys(cleanedFilter).length > 0) {
            newDashboard.filter = cleanedFilter;
        }

        model = {
            ...settings,
            dashboard: [...(settings.dashboard || []), newDashboard],
        };
    }

    return model;
    }catch(e){
    console.error(e)
    }
    
}


export const DashboardWidget = ({  state, filterComponent, loaderCount = 8 }: any) => {
  const { isLoading, error, widgetData, widget, filter } = state;
  const { openToast } = useToastStore();
   const { t } = useTranslation();

  const { mutation: saveMutation } = useUpdateUserSettings({
    onSuccess: () => openToast('dashboard', 'Success', t('dashboard.settings.sucessMessage') as any),
    onError: () => openToast('dashboard', 'Error', t('dashboard.settings.failureMessage') as any),
  });

  const handleSaveWidget = useCallback(async (widget: any,filter: any) => {
    try {
      const { userSetting } = useUserSettingStore.getState();
      const model = getUpdatedWidgetModel(widget.dashboardId, userSetting, widget, filter);
      saveMutation.mutate(model);
    } catch (e) {
      console.error(e);
    }
  }, [saveMutation]);


  if (isLoading) {
    return (
      <div style={styles.cell}>
        <MultiViewWidgetContainer>
          <MultiViewWidgetHeader>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '12px',
                padding: '12px',
              }}
            >
              <div>
                <WidgetTitle>{widget?.title}</WidgetTitle>
              </div>
              <div style={{ display: 'flex' }}>
                <div style={{ marginTop: '-2px' }}></div>
                <div>
                  <div
                    title="Threshold Settings"
                    style={{
                      marginTop: '4px',
                      padding: '1px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </MultiViewWidgetHeader>
          <div className="widget-content">
            <div
              style={{
                gap: '2px',
              }}
            >
              <SkeletonLoader count={loaderCount} />
            </div>
          </div>
        </MultiViewWidgetContainer>
      </div>
    );
  }

  if (!widget) return null;

  return (
       <div style={styles.cell}>
        <MultiViewWidget
          data={widgetData?.chartData}
          widget={widget}
          filter={filter}
          error={error}
          onSettingSave={handleSaveWidget}
          filterComponent={filterComponent}
        />
        </div>
  );
};
